import {CommonModule, DOCUMENT} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {AlertDialogService} from '../../shared/commons/dialog/alert-dialog/alert-dialog.service';
import {ToastService} from '../../shared/commons/toast/toast-service';
import {ConfirmDialogService} from '../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {SurveyApiService, SurveyQuestionDto} from './survey-api.service';

interface SurveyQuestionItem extends SurveyQuestionDto {
  answerKey: string;
}

@Component({
  selector: 'app-survey',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './survey.html',
  styleUrl: './survey.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Survey implements OnInit {
  protected readonly questions = signal<SurveyQuestionItem[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly isConfirming = signal(false);
  protected readonly successMessage = signal('');
  protected readonly errorMessage = signal('');
  protected readonly hasDuplicateQuestionIds = signal(false);
  protected readonly scoreOptions = [5, 4, 3, 2, 1];
  protected readonly scoreLabels: Record<number, string> = {
    5: 'มากที่สุด',
    4: 'มาก',
    3: 'น้อย',
    2: 'น้อยที่สุด',
    1: 'ไม่พึงพอใจ'
  };
  protected readonly form: FormGroup;
  private readonly document = inject(DOCUMENT);

  constructor(
    private readonly fb: FormBuilder,
    private readonly surveyApi: SurveyApiService,
    private readonly toast: ToastService,
    private readonly alertDialog: AlertDialogService,
    private readonly confirmDialog: ConfirmDialogService
  ) {
    this.form = this.fb.group({
      suggestion: [''],
      answers: this.fb.group({})
    });
  }

  ngOnInit(): void {
    this.loadQuestions();
  }

  async saveSurvey(): Promise<void> {
    if (this.questions().length === 0) {
      this.errorMessage.set('ยังไม่มีรายการคำถาม');
      return;
    }
    if (this.hasDuplicateQuestionIds()) {
      this.errorMessage.set('ไม่สามารถส่งแบบสอบถามได้ เนื่องจากข้อมูลคำถามมีรหัสซ้ำ กรุณาติดต่อผู้ดูแลระบบ');
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.errorMessage.set('กรุณาให้คะแนนให้ครบทุกข้อ');
      this.focusFirstInvalidAnswer();
      return;
    }

    this.isConfirming.set(true);
    const confirmed = await this.confirmSubmission();
    this.isConfirming.set(false);
    if (!confirmed) {
      return;
    }

    const answersGroup = this.form.get('answers') as FormGroup;
    const answers = this.questions().reduce<Record<string, number>>((result, question) => {
      const point = answersGroup.get(question.answerKey)?.value;
      if (point !== null && point !== undefined) {
        result[question.id] = Number(point);
      }
      return result;
    }, {});

    this.isSaving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');

    this.surveyApi.submitSurvey({
      suggestion: `${this.form.get('suggestion')?.value ?? ''}`.trim(),
      question: answers
    }).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.successMessage.set('บันทึกแบบสอบถามเรียบร้อยแล้ว');
        this.toast.success('บันทึกแบบสอบถามเรียบร้อยแล้ว');
        this.resetFormState();
      },
      error: () => {
        this.isSaving.set(false);
        this.showSubmissionFailureDialog();
      }
    });
  }

  protected isAnswerInvalid(answerKey: string): boolean {
    const control = this.form.get(['answers', answerKey]);
    return Boolean(control?.invalid && control.touched);
  }

  private loadQuestions(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.surveyApi.getQuestions()
      .subscribe({
        next: (items) => {
          const questionItems = this.createQuestionItems(items);
          this.questions.set(questionItems);
          this.rebuildAnswerControls(questionItems);
          this.hasDuplicateQuestionIds.set(this.containsDuplicateQuestionIds(items));
          if (this.hasDuplicateQuestionIds()) {
            this.errorMessage.set('ไม่สามารถส่งแบบสอบถามได้ เนื่องจากข้อมูลคำถามมีรหัสซ้ำ กรุณาติดต่อผู้ดูแลระบบ');
          }
          this.isLoading.set(false);
        },
        error: () => {
          this.questions.set([]);
          this.rebuildAnswerControls([]);
          this.hasDuplicateQuestionIds.set(false);
          this.isLoading.set(false);
          this.showLoadFailureDialog();
        }
      });
  }

  private showLoadFailureDialog(): void {
    this.alertDialog.open({
      type: 'warning',
      isInnerHTML: true,
      content: '<div class="text-center">'
        + '<div class="mb-2 fw-semibold">ไม่สามารถโหลดแบบสอบถามได้ในขณะนี้</div>'
        + '<div class="text-body-secondary">ปัญหาเกิดจากระบบภายนอกที่เชื่อมต่อด้วย (e-Portal)'
        + '<br> กรุณาลองใหม่อีกครั้งในภายหลัง</div>'
        + '</div>',
      confirmButtonMsg: 'ปิด'
    });
  }

  private showSubmissionFailureDialog(): void {
    this.alertDialog.open({
      type: 'danger',
      isInnerHTML: true,
      content: '<div class="text-center">'
        + '<div class="mb-2 fw-semibold">ไม่สามารถบันทึกแบบสอบถามได้</div>'
        + '<div class="text-body-secondary">เกิดข้อผิดพลาดจากระบบ กรุณาลองใหม่อีกครั้งในภายหลัง</div>'
        + '</div>',
      confirmButtonMsg: 'ปิด'
    });
  }

  private confirmSubmission(): Promise<boolean> {
    return firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการส่งแบบสอบถาม',
      content: 'คุณต้องการส่งแบบสอบถามนี้ใช่หรือไม่?',
      confirmButtonMsg: 'ส่งแบบสอบถาม',
      closeButtonMsg: 'ตรวจสอบอีกครั้ง'
    }));
  }

  private createQuestionItems(questions: SurveyQuestionDto[]): SurveyQuestionItem[] {
    return questions.map((question, index) => ({
      ...question,
      answerKey: `question-${index}`
    }));
  }

  private containsDuplicateQuestionIds(questions: SurveyQuestionDto[]): boolean {
    return new Set(questions.map((question) => question.id)).size !== questions.length;
  }

  private focusFirstInvalidAnswer(): void {
    const firstInvalidQuestion = this.questions().find((question) => this.isAnswerInvalid(question.answerKey));
    if (!firstInvalidQuestion) {
      return;
    }
    const firstScoreId = `survey-score-${firstInvalidQuestion.answerKey}-${this.scoreOptions[0]}`;
    (this.document.getElementById(firstScoreId) as HTMLInputElement | null)?.focus();
  }

  private rebuildAnswerControls(questions: SurveyQuestionItem[]): void {
    const answersGroup = this.fb.group({});
    questions.forEach((question) => {
      answersGroup.addControl(question.answerKey, this.fb.control(null, Validators.required));
    });
    this.form.setControl('answers', answersGroup);
    this.form.get('suggestion')?.reset('');
  }

  private resetFormState(): void {
    this.rebuildAnswerControls(this.questions());
  }
}
